import _extends from "@babel/runtime/helpers/esm/extends";
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useRef } from 'react';
import { KEY_CODES } from '../../constants';
import { focusNextElement, focusPreviousElement } from '../../util/focus';
import { MenuContext } from './Menu.context';
import { StyledMenu } from './Menu.styles';

/**
 * Keyboard interaction is different from dropdowns in that you navigate the menu with the arrow
 * keys as opposed to with tab. Actions can be selected with enter or space.
 *
 * [ARIA Authoring Practices: Menu](https://www.w3.org/WAI/ARIA/apg/patterns/menu/)
 *
 * @extends Dropdown
 * @subcomponents ['MenuContent', 'MenuHeading', 'MenuItem', 'MenuPopper']
 * @since 7.28.0
 */
export var Menu = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var defaultInnerRef = useRef();
  var innerRef = forwardedRef || defaultInnerRef;
  var activeElement = useRef();
  var onBeforeTakeFocus = props.onBeforeTakeFocus,
    onClose = props.onClose;

  // Capture the original focus element so we can handle tab navigation after taking focus
  var onBeforeTakeFocusCallback = useCallback(function () {
    if (typeof document !== 'undefined') {
      activeElement.current = document.activeElement;
    }
    if (onBeforeTakeFocus) {
      onBeforeTakeFocus();
    }
  }, [onBeforeTakeFocus]);
  var onKeyDownCallback = useCallback(function (e) {
    if (e.keyCode === KEY_CODES.ARROW_DOWN) {
      focusNextElement(innerRef.current);
      e.preventDefault();
    } else if (e.keyCode === KEY_CODES.ARROW_UP) {
      focusPreviousElement(innerRef.current);
      e.preventDefault();
    } else if (e.keyCode === KEY_CODES.TAB) {
      // Restore focus to the trigger and let default behavior take over
      if (activeElement.current) {
        activeElement.current.focus();
      }
      if (onClose) {
        onClose();
      }
    }
  }, [innerRef, onClose]);
  return /*#__PURE__*/React.createElement(MenuContext.Provider, {
    value: {
      onActivate: onClose
    }
  }, /*#__PURE__*/React.createElement(StyledMenu, _extends({
    innerRef: innerRef
  }, props, {
    onKeyDown: onKeyDownCallback,
    onBeforeTakeFocus: onBeforeTakeFocusCallback
  })));
});
Menu.displayName = 'Menu';
Menu.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Inherited from Dialog.
   */
  onBeforeTakeFocus: PropTypes.func,
  /**
   * Function called when the menu should closed, e.g. when a `MenuItem` is clicked.
   *
   * Inherited from Dialog.
   */
  onClose: PropTypes.func,
  /**
   * Menus have the role of `"menu"` by default per the
   * [accessibility guidelines](https://www.w3.org/WAI/ARIA/apg/example-index).
   */
  role: PropTypes.string
} : {};
Menu.defaultProps = {
  onBeforeTakeFocus: undefined,
  onClose: undefined,
  role: 'menu'
};